// BeaconFormModal.tsx — add a beacon to a sheet, or rename / change its interval.

import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Check, Clock, Link2, Loader2, Radar, Tag, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { Banner, SheetGlyph, TONES } from '../../SheetsManager/parts/SheetImportDetailsModal';
import { DEFAULT_INTERVAL, INTERVAL_OPTIONS, SheetBeacon, apiError, beaconApi } from './beaconTypes';

const SHELL: React.CSSProperties = { background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' };
const INNER: React.CSSProperties = { background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px' };
const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: C.teal,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '5.5px',
};
const SHEET_URL_RE = /\/spreadsheets\/d\/[a-zA-Z0-9-_]+/;
const NAME_MAX = 80;

interface BeaconFormModalProps {
  visible: boolean;
  /** null → create, a beacon → edit */
  beacon: SheetBeacon | null;
  onHide: () => void;
  /** warning is set when the beacon was created but its first check failed */
  onSaved: (b: SheetBeacon, warning?: string) => void;
}

const BeaconFormModal: React.FC<BeaconFormModalProps> = ({ visible, beacon, onHide, onSaved }) => {
  const isEdit = !!beacon;
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [interval, setEvery] = useState(DEFAULT_INTERVAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setUrl(beacon?.sheet_url ?? '');
    setName(beacon?.display_name ?? '');
    setEvery(beacon?.interval_minutes ?? DEFAULT_INTERVAL);
    setError(null);
  }, [visible, beacon]);

  const handleHide = () => {
    if (!saving) onHide();
  };

  const submit = async () => {
    const u = url.trim();
    if (!isEdit) {
      if (!u) return setError('Paste a Google Sheets link first.');
      if (!SHEET_URL_RE.test(u)) return setError("That doesn't look like a Google Sheets link. It should contain /spreadsheets/d/…");
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const res = await beaconApi.update(beacon!.id, { display_name: name.trim(), interval_minutes: interval });
        onSaved(res.data);
      } else {
        const res = await beaconApi.create({ sheet_url: u, display_name: name.trim() || undefined, interval_minutes: interval });
        onSaved(res.data, res.warning);
      }
      onHide();
    } catch (e) {
      setError(apiError(e, isEdit ? 'Could not update the beacon.' : 'Could not create the beacon.'));
    } finally {
      setSaving(false);
    }
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Radar size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          {isEdit ? 'Edit beacon' : 'Add a sheet beacon'}
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {isEdit ? 'Rename it or change how often it checks' : 'Get told when new leads land in a Google Sheet'}
        </div>
      </div>
    </div>
  );

  const inputBox = (disabled: boolean, invalid = false): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '40px',
    padding: '0 11px',
    background: disabled ? '#f9fafb' : C.white,
    border: `1px solid ${invalid ? 'rgba(248,113,113,0.9)' : G.line}`,
    borderRadius: '8.3px',
  });
  const inputStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.79rem',
    color: '#111827',
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="bfm-dialog"
      maskClassName="bfm-mask"
      style={{ width: '90vw', maxWidth: '520px' }}
      modal
      closable={!saving}
      dismissableMask={!saving}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .bfm-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: bfmMaskIn 0.3s ease; }
        .bfm-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: bfmDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
        .bfm-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .bfm-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .bfm-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 76vh; overflow-y: auto; }
        @keyframes bfmMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bfmDlgIn { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .bfm-chip:hover:not(:disabled) { border-color: rgba(17,85,80,0.35) !important; }
        .bfm-spin { animation: bfmSpin 0.9s linear infinite; }
        @keyframes bfmSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Sheet link */}
            <div>
              <label style={FIELD_LABEL} htmlFor="bfm-url">
                Google Sheet link <span style={{ color: '#d93025' }}>*</span>
              </label>
              <div style={inputBox(isEdit || saving, !!error && !isEdit)}>
                <SheetGlyph size={15} color="#16a34a" />
                <input
                  id="bfm-url"
                  value={url}
                  disabled={isEdit || saving}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  style={inputStyle}
                  autoComplete="off"
                />
                <Link2 size={14} color={C.muted} />
              </div>
              <div style={{ fontSize: '0.64rem', color: C.muted, marginTop: '5px' }}>
                {isEdit
                  ? 'The link can’t change. Delete this beacon and add a new one to watch another sheet.'
                  : 'Share the sheet as “Anyone with the link → Viewer”, same as Sheet Sync.'}
              </div>
            </div>

            {/* Display name */}
            <div>
              <label style={FIELD_LABEL} htmlFor="bfm-name">
                Display name <span style={{ fontWeight: 500, color: C.muted, textTransform: 'none' }}>(optional)</span>
              </label>
              <div style={inputBox(saving)}>
                <Tag size={14} color={C.muted} />
                <input
                  id="bfm-name"
                  value={name}
                  disabled={saving}
                  maxLength={NAME_MAX}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !saving && submit()}
                  placeholder="e.g. Diwali Meta Leads"
                  style={inputStyle}
                  autoComplete="off"
                />
                <span style={{ fontSize: '0.6rem', color: C.muted }}>
                  {name.length}/{NAME_MAX}
                </span>
              </div>
              <div style={{ fontSize: '0.64rem', color: C.muted, marginTop: '5px' }}>
                Only a label. Renaming never breaks filters — the beacon is tied to the sheet’s ID.
              </div>
            </div>

            {/* Interval */}
            <div>
              <label style={FIELD_LABEL}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={11} /> Check every
                </span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {INTERVAL_OPTIONS.map((o) => {
                  const on = interval === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      title={o.label}
                      disabled={saving}
                      onClick={() => setEvery(o.value)}
                      className="bfm-chip"
                      aria-pressed={on}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: on ? 800 : 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        background: on ? C.lime : C.white,
                        color: on ? C.teal : C.sub,
                        border: `1.5px solid ${on ? C.limeBorder : G.line}`,
                        boxShadow: on ? '0 2px 8px rgba(200,233,75,0.35)' : 'none',
                      }}
                    >
                      {on && <Check size={11} strokeWidth={3} />} {o.short}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {!isEdit && (
          <Banner tone={TONES.lime} icon={<Radar size={15} />}>
            A beacon counts rows that aren't in Leads yet. Rows already pushed are never counted again, even if the sheet is sorted
            or rows are added in the middle. It only tells you — your leads are still added through Sheet Sync.
          </Banner>
        )}

        {error && (
          <Banner tone={TONES.red} icon={<X size={15} />}>
            {error}
          </Banner>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleHide} disabled={saving} />
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.3px',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.85 : 1,
              boxShadow: '0 4px 12px rgba(17,85,80,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            {saving ? <Loader2 size={14} className="bfm-spin" /> : isEdit ? <Check size={14} strokeWidth={3} /> : <Radar size={14} />}
            {saving ? (isEdit ? 'SAVING…' : 'READING SHEET…') : isEdit ? 'SAVE' : 'START BEACON'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default BeaconFormModal;
